import { FC, useEffect } from "react";
import LABELS from "../../../../constants/labels/pump.labels";
import Dropdown from "../../../common/dropdown";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../store/hooks/StoreHooks";
import useCollapseFacade from "../../../../facade/collapse.Facade";
import Accordion from "../../../common/accordion";
import {
  setHiddenHousingType,
  setHiddenShaftType,
} from "../../../../store/calculation/calculation-input/TubingSlice";



interface PumpPressureShaftProps {
  setShowError: React.Dispatch<React.SetStateAction<string[]>>;
  showError: string[];
}

const getShaftRatingLabel = (
  shaftType: string | undefined,
  frequency: string | number | undefined
) => {
  if (!shaftType) return LABELS.shaftRating;

  const freqValue = frequency?.toString() ?? '50';

  return shaftType === "0"
    ? `Shaft rating standard @ ${freqValue} Hz`
    : `Shaft rating HS @ ${freqValue} Hz`;
};

const PumpPressureShaft: FC<PumpPressureShaftProps> = ({ showError, setShowError }) => {
  const dispatch = useAppDispatch();
  const { maximumPressureCollapse } = useAppSelector(
    (state) => state?.collapse
  );

  const { updateMaximumPressureCollapse } = useCollapseFacade();

  const handleCollapseChange = (key: boolean) => {
    updateMaximumPressureCollapse(key);
  };

  const pumpPressureShaft = useAppSelector(
    (state) => state.pump.PumpPressureShaft
  );
  const pumpInput = useAppSelector((state) => state.pump.PumpInput);
  const selectedPumpInfo = useAppSelector((state) => state.pump);
  const selectedESPPumpEquipments = useAppSelector(
    (state) => state.pump.ESPEquipments
  );

  const pumpInputCalculated = useAppSelector(
    (state) => state.pump.PumpInputCalculated
  );
  const tubingDetails = useAppSelector((state) => state.tubing.tab1Input);
  const fluidDetails = useAppSelector((state) => state.fluid.tab3Input);

  const handleSelect = (option: any, type: string) => {
    if (type === "hdn_Pump_HousingType") {
      dispatch(setHiddenHousingType(option.id));
    } else if (type === "hdn_Pump_ShaftType") {
      dispatch(setHiddenShaftType(option.id));
    }
  };
  useEffect(() => {
    const housingLoad = selectedPumpInfo?.FetchPercentageHousingLoad;
    const shaftLoad = selectedPumpInfo?.FetchShaftPercentageLoad;

    const housingError = "Pressure generated by pump exceeds housing pressure limit";
    const shaftError = "Pump Shaft not suitable for required power";

    setShowError((prevErrors) => {
      let updatedErrors = [...prevErrors];

      // Housing Load check
      if (housingLoad > 100) {
        if (!updatedErrors.includes(housingError)) {
          updatedErrors.push(housingError);
        }
      } else {
        updatedErrors = updatedErrors.filter((e) => e !== housingError);
      }

      // Shaft Load check
      if (shaftLoad > 100) {
        if (!updatedErrors.includes(shaftError)) {
          updatedErrors.push(shaftError);
        }
      } else {
        updatedErrors = updatedErrors.filter((e) => e !== shaftError);
      }

      return updatedErrors;
    });
  }, [
    selectedPumpInfo?.FetchPercentageHousingLoad,
    selectedPumpInfo?.FetchShaftPercentageLoad,
    setShowError,
  ]);
  return (
    <>{pumpPressureShaft && pumpInput && tubingDetails &&<Accordion
      title='Maximum pressure & shaft rating'
      isOpen={maximumPressureCollapse}
      onToggle={() => handleCollapseChange(!maximumPressureCollapse)}
      content={
        <div className='flex justify-center items-start p-4 gap-4 w-full'>
          <div className='flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2'>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <h1 className='text-neutral-25 text-left py-2 font-semibold'>Housing Pressure</h1>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>

              <label className='text-neutral-25 text-[14px] font-normal'>
                {LABELS.typeOfHousing}
              </label>
              <label className='text-neutral-25'>
                <div className="w-[200px]"> {/* Increased width for both dropdowns */}
                  <Dropdown
                    options={[
                      { id: "0", name: "Standard" },
                      { id: "1", name: "High Pressure" },
                    ]}
                    onSelect={(option) =>
                      handleSelect(option, "hdn_Pump_HousingType")
                    }
                    dropdownBgColor='bg-darkcolor-800'
                    bgColor='bg-transparent'
                    selected={tubingDetails?.hdn_Pump_HousingType}
                    width="200px" // Add explicit width
                    autoClose={true} // Optional, defaults to true
                  />
                </div>
              </label>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>
                {LABELS.housingPressureLimit}
              </label>
              <label className='text-neutral-25'>
                {pumpPressureShaft.HousingPressureLimit}
                <span className="ml-1">{pumpInput.actNamePump_PressureLimit}</span>
              </label>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>{LABELS.pmax}</label>
              <label className='text-neutral-25'>
                {pumpPressureShaft.Pmax}
                <span className="ml-1">{pumpInput.actNamePump_Pmax}</span>
              </label>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>
                {LABELS.percentHousingLoad}
              </label>
              <label className='text-neutral-25'>
                {selectedPumpInfo?.FetchPercentageHousingLoad}<span className="ml-1">%</span>
              </label>
            </div>
          </div>
          <div className='flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2 align-top'>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <h1 className='text-neutral-25 text-left py-2 font-semibold'>Shaft rating</h1>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>{LABELS.typeOfShaft}</label>
              <label className='text-neutral-25'>
                {" "}
                <div className="w-[200px]"> {/* Increased width for both dropdowns */}
                  <Dropdown
                    options={[
                      { id: "0", name: "Standard" },
                      { id: "1", name: "High Strength" },
                    ]}
                    onSelect={(option) =>
                      handleSelect(option, "hdn_Pump_ShaftType")
                    }
                    dropdownBgColor='bg-darkcolor-800'
                    bgColor='bg-transparent'
                    selected={tubingDetails?.hdn_Pump_ShaftType}
                    width="200px" // Add explicit width
                    autoClose={true} // Optional, defaults to true
                  />
                </div>
              </label>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>
                {getShaftRatingLabel(tubingDetails?.hdn_Pump_ShaftType, fluidDetails?.operatngFreq)}
              </label>
              <label className='text-neutral-25'>
                {selectedPumpInfo?.FetchShaftRating}
                <span className="ml-1">hp</span>
              </label>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>
                {LABELS.power} @ {fluidDetails?.operatngFreq}
                {pumpInput.actNamePump_OperatingFrequency}
              </label>
              <label className='text-neutral-25'>
                {pumpInputCalculated.hPOPPump} hp
              </label>
            </div>
            <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
              <label className='text-neutral-25'>
                {LABELS.percentShaftLoad}
              </label>
              <label className='text-neutral-25'>
                {selectedPumpInfo?.FetchShaftPercentageLoad}
                <span className="ml-1">%</span>
              </label>
            </div>
          </div>
        </div>
      }
    />
    }
    </>
  );
};

export default PumpPressureShaft;
